import React, { useEffect, useState, useRef } from 'react';
import { MoreVertical } from './Icons.jsx';

/** In-App-Meldung (ersetzt Browser-alert). type: 'success' | 'error' | 'info' */
export function Toast({ message, type = 'info', onClose }) {
    useEffect(() => {
        if (!message) return;
        const t = setTimeout(() => onClose?.(), 4500);
        return () => clearTimeout(t);
    }, [message, onClose]);

    if (!message) return null;
    const bg = type === 'error' ? 'bg-[#FF3B30]' : type === 'success' ? 'bg-[#34C759]' : 'bg-[#1C1C1E]/90 backdrop-blur-md';
    return (
        <div
            className={`fixed top-safe-t left-1/2 -translate-x-1/2 z-[9999] mt-2 ${bg} text-white px-5 py-3 rounded-[20px] shadow-lg max-w-[90vw] sm:max-w-md flex items-center justify-between gap-4`}
            role="alert"
        >
            <span className="text-[15px] font-medium tracking-tight">{message}</span>
            <button type="button" onClick={onClose} className="text-white/80 hover:text-white p-1 -m-1" aria-label="Schließen">
                ×
            </button>
        </div>
    );
}

/** In-App-Bestätigungsdialog (ersetzt Browser-confirm). onConfirm/onCancel werden einmal aufgerufen. confirmLabel/cancelLabel optional. */
export function ConfirmModal({ isOpen = true, message, onConfirm, onCancel, onClose, confirmLabel = 'OK', cancelLabel = 'Abbrechen' }) {
    if (!isOpen || !message) return null;
    const parts = String(message)
        .split(/\n\s*\n/)
        .map((p) => p.trim())
        .filter(Boolean);
    const title = parts[0] || '';
    const description = parts.slice(1).join('\n\n');

    return (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4 bg-slate-900/45 backdrop-blur-[2px]" role="dialog" aria-modal="true" aria-labelledby="confirm-title" onClick={() => (onClose ? onClose() : onCancel?.())}>
            <div className="w-full max-w-xl bg-white rounded-[24px] border border-slate-200/70 shadow-[0_20px_60px_rgba(15,23,42,0.22)] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 sm:px-7 pt-6 sm:pt-7 pb-5">
                    <p id="confirm-title" className="text-slate-900 font-semibold text-[17px] sm:text-[18px] tracking-tight whitespace-pre-line">
                        {title}
                    </p>
                    {description && (
                        <p className="mt-3 text-[13px] leading-relaxed text-slate-500 whitespace-pre-line">
                            {description}
                        </p>
                    )}
                </div>
                <div className="px-6 sm:px-7 pb-6 sm:pb-7 flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5 border-t border-slate-100 pt-4">
                    <button type="button" onClick={() => { onCancel?.(); }} className="w-full sm:w-auto inline-flex items-center justify-center min-h-[42px] px-4 py-2 rounded-full border border-slate-300 text-slate-700 text-[14px] font-medium hover:bg-slate-50 transition-colors">
                        {cancelLabel}
                    </button>
                    <button type="button" onClick={() => { onConfirm?.(); }} className="w-full sm:w-auto inline-flex items-center justify-center min-h-[42px] px-4 py-2 rounded-full bg-[#007AFF] text-white text-[14px] font-semibold hover:opacity-90 transition-all">
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

export function Card({ children, className = '', onClick }) {
    return (
        <div
            onClick={onClick}
            className={`bg-white rounded-[20px] shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-200/50 ${className} ${onClick ? 'cursor-pointer active:scale-[0.98] active:opacity-90 transition-all' : ''
                }`}
        >
            {children}
        </div>
    );
}

const statusStyles = {
    draft: 'bg-slate-100 text-slate-600',
    sent: 'bg-blue-100 text-blue-700',
    paid: 'bg-green-100 text-green-700',
    overdue: 'bg-red-100 text-red-700',
    accepted: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
    active: 'bg-green-100 text-green-700',
    inactive: 'bg-slate-100 text-slate-500',
    cancelled: 'bg-amber-100 text-amber-700',
    credited: 'bg-violet-100 text-violet-700',
};

const statusLabels = {
    draft: 'Entwurf',
    sent: 'Gesendet',
    paid: 'Bezahlt',
    overdue: 'Überfällig',
    accepted: 'Angenommen',
    rejected: 'Abgelehnt',
    active: 'Aktiv',
    inactive: 'Beendet',
    cancelled: 'Storniert',
    credited: 'Gutgeschrieben',
};

export function Badge({ status }) {
    return (
        <span
            className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[status] || statusStyles.draft}`}
        >
            {statusLabels[status] || status}
        </span>
    );
}

export function ActionMenu({ primaryActions = [], menuActions = [] }) {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef(null);
    const triggerRef = useRef(null);
    const [menuStyle, setMenuStyle] = useState({});

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target) && (!triggerRef.current || !triggerRef.current.contains(event.target))) {
                setIsOpen(false);
            }
        };
        const handleScroll = () => {
            if (isOpen) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true); // Use capture phase to catch internal scrolls
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [isOpen]);

    const handleToggle = (e) => {
        e.stopPropagation();
        if (!isOpen && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            // Position fixed relative to viewport
            setMenuStyle({
                position: 'fixed',
                top: `${rect.bottom + 4}px`,
                right: `${document.documentElement.clientWidth - rect.right}px`,
                zIndex: 9999,
            });
        }
        setIsOpen(!isOpen);
    };

    return (
        <div className="relative flex items-center gap-1" ref={menuRef}>
            {primaryActions.map((action, i) => (
                <button
                    key={i}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); action.onClick(); setIsOpen(false); }}
                    title={action.title}
                    className={`p-2 rounded-lg transition-colors ${action.className || 'text-slate-500 hover:bg-slate-100'}`}
                >
                    <action.icon size={18} />
                </button>
            ))}
            {menuActions.length > 0 && (
                <>
                    <button
                        ref={triggerRef}
                        type="button"
                        onClick={handleToggle}
                        className={`p-2 rounded-lg transition-colors ${isOpen ? 'bg-slate-100 text-slate-800' : 'text-slate-500 hover:bg-slate-100'}`}
                    >
                        <MoreVertical size={18} />
                    </button>
                    {isOpen && (
                        <div
                            className="bg-white rounded-xl shadow-lg border border-slate-100 py-1 w-56"
                            style={menuStyle}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {menuActions.map((action, i) => {
                                if (action.divider) return <div key={i} className="border-t border-slate-100 my-1" />;
                                return (
                                    <button
                                        key={i}
                                        onClick={() => { action.onClick(); setIsOpen(false); }}
                                        className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 hover:bg-slate-50 ${action.className || 'text-slate-700'}`}
                                    >
                                        {action.icon && <action.icon size={16} />}
                                        {action.title}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
